<template>
<m-pageframe
    title="{organization-label}: RedChirp Billing Settings"
>
<v-container
    fluid
    class="pa-4 pa-sm-6"
    :class="$vuetify.breakpoint.mdAndUp ? 'max-width-1200' : ''"
>
    <!-- Page Introduction -->
    <div class="mb-8">
        <h1 class="text-h4 font-weight-medium mb-2">Billing Settings</h1>
        <p class="text-subtitle-1 grey--text text--darken-1">
            Manage your payment methods, view invoices, and update billing preferences for your RedChirp account.
        </p>
    </div>

    <!-- Billing Preferences Section -->
    <v-card class="mb-6" outlined>
        <v-card-title class="grey lighten-4">
            <v-icon left color="primary">mdi-email-outline</v-icon>
            Billing Preferences
        </v-card-title>
        <v-card-text class="pt-4">
            <p class="text-body-2 grey--text text--darken-1 mb-4">
                Configure where your invoices are sent
            </p>
            <m-redchirp-payment-org-controlled-billing-settings
                :organization_id="$route.params.organization_id"
            />
        </v-card-text>
    </v-card>

    <!-- Payment Methods Section -->
    <v-card class="mb-6" outlined>
        <v-card-title class="grey lighten-4">
            <v-icon left color="primary">mdi-credit-card-outline</v-icon>
            Payment Methods
        </v-card-title>
        <v-card-text class="pt-4">
            <p class="text-body-2 grey--text text--darken-1 mb-4">
                Manage the payment methods associated with your account. Your active payment method will be used for automatic billing.
            </p>
            <m-redchirp-payment-methods
                :organization_id="$route.params.organization_id"
            />
        </v-card-text>
    </v-card>

    <!-- Invoices Section -->
    <v-card
        class="mb-6"
        outlined
        v-if="$rc.$state.isOrganizationAdmin()"
    >
        <v-card-title class="grey lighten-4">
            <v-icon left color="primary">mdi-file-document-outline</v-icon>
            Invoices
        </v-card-title>
        <v-card-text class="pt-4">
            <p class="text-body-2 grey--text text--darken-1 mb-4">
                View and download your RedChirp invoices.
            </p>

            <!-- Loading State -->
            <div v-if="loading_invoices" class="d-flex flex-column align-center py-6">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                <div class="mt-2 grey--text">Loading invoices...</div>
            </div>

            <!-- Empty State -->
            <v-card
                v-else-if="invoices && invoices.length === 0"
                outlined
                class="text-center pa-6 grey lighten-5"
            >
                <v-icon size="64" color="grey lighten-1" class="mb-2">mdi-file-document-outline</v-icon>
                <div class="text-subtitle-1 grey--text text--darken-1">No invoices found</div>
            </v-card>

            <!-- Invoice Table -->
            <v-data-table
                v-else-if="invoices != null"
                :headers="invoice_headers"
                :items="invoices"
                :items-per-page="10"
                class="mt-2"
                :footer-props="{
                    itemsPerPageOptions: [5, 10, 25, 50],
                }"
            >


                <template v-slot:item.action="{ item }">
                    <v-btn
                        small
                        outlined
                        color="primary"
                        @click="onClickOpenInvoice(item)"
                    >
                        <v-icon left small>mdi-download</v-icon>
                        View
                    </v-btn>
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>

    <!-- W-9 Form Section -->
    <v-card class="mb-6" outlined>
        <v-card-title class="grey lighten-4">
            <v-icon left color="primary">mdi-file-outline</v-icon>
            Tax Documents
        </v-card-title>
        <v-card-text class="pt-4">
            <p class="text-body-2 grey--text text--darken-1 mb-4">
                Access tax-related documents for your records.
            </p>
            <v-btn
                outlined
                color="primary"
                href="https://files.redchirp.com/443ce6cd-115b-46aa-bf10-54c1ca1fd651.pdf"
                target="_blank"
            >
                <v-icon left>mdi-file-pdf-box</v-icon>
                Download RedChirp W-9 Form
            </v-btn>
        </v-card-text>
    </v-card>

    <!-- Admin Section -->
    <v-card
        v-if="this.$rc.$state.isAuthActorSystemAdmin()"
        outlined
        class="mb-6"
        :class="{'border-left-orange': $vuetify.breakpoint.mdAndUp}"
    >
        <v-card-title class="grey lighten-4">
            <v-icon left color="orange darken-1">mdi-shield-account</v-icon>
            Administrator Tools
        </v-card-title>
        <v-card-text class="pt-4">
            <v-alert
                type="info"
                outlined
                class="mb-4"
                dense
            >
                <p class="mb-2 font-weight-medium">Best Practice for Payment Collection:</p>
                <p class="mb-1">Collect payment method information from clients using the secure payment link below. This link does not require the recipient to log into RedChirp. Please note that this link is organization specific.</p>
                <p class="mb-0">For manual entry, open the link in a private browsing window and enter the payment information as the customer would.</p>
            </v-alert>

            <m-edit-text
                label="Secure Payment Method Link"
                :value="$rc.linkProvideRedChirpPaymentMethod($router, $route.params.organization_id)"
                read_only
            />
        </v-card-text>
    </v-card>

</v-container>
</m-pageframe>
</template>

<script>
export default {

    data() {
        return {
            invoices: [],
            loading_invoices: true,
            invoice_headers: [
                { text: 'Date', value: 'invoice_date'},
                { text: 'Invoice Number', value: 'doc_number'},
                { text: 'Customer Name', value: 'intuit_customer_name'},
                { text: 'Action', value: 'action', sortable: false, align: 'center' },
            ],
        }
    },

    mounted () {
        this.updateInvoices();
    },

    methods: {
        updateInvoices() {
            this.loading_invoices = true;
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'LIST: /api/v1/integrations/invoices',

                'params' : {
                        'organization_id': this.$route.params.organization_id,
                    },

                'on_success': (data) => {
                        this.invoices = data.results;
                        this.loading_invoices = false;
                    },

                'on_error': (error) => {
                    this.$rc.alert('Error', 'Unable to list invoices: ' + error);
                    this.loading_invoices = false;
                },
            });
        },

        onClickOpenInvoice(item) {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/integrations/invoices/download',

                'params' : {
                        'invoice_id': item.invoice_id,
                        'timeout': 30*60*1000,
                    },

                'on_success': (data) => {
                        window.open(data.invoice_pdf_url, '_blank');
                    },

                'on_error': 'Unable to download invoice PDF',
            });
        },
    },
}
</script>